import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/98 backdrop-blur-md'
          : 'bg-background-50/90 backdrop-blur-sm'
      }`}
    >
      <div className="px-4 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group flex-shrink-0"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-primary-500 flex items-center justify-center overflow-hidden transition-transform duration-300 group-hover:scale-105">
              <span className="text-background-50 text-sm font-bold tracking-tight font-heading">P</span>
            </div>
            <span className="text-foreground-950 font-semibold text-sm md:text-base tracking-tight font-heading">
              pandabuy<span className="text-accent-500">.sbs</span>
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              className="text-foreground-800 hover:text-foreground-950 text-sm font-semibold transition-colors duration-200 whitespace-nowrap"
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center gap-1.5 text-foreground-800 hover:text-foreground-950 text-sm font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Mega Dropdown */}
              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[640px] bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 origin-top ${
                  dropdownOpen
                    ? 'opacity-100 scale-100 pointer-events-auto'
                    : 'opacity-0 scale-95 pointer-events-none'
                }`}
                style={{
                  boxShadow: '0 20px 60px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div className="p-5">
                  <div className="grid grid-cols-4 gap-x-6 gap-y-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-foreground-700 hover:text-accent-500 hover:bg-accent-50 transition-all duration-200 group/item whitespace-nowrap"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-sm group-hover/item:translate-x-0.5 transition-transform duration-200`}></i>
                        </span>
                        <span className="text-sm font-medium">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
                <div className="px-5 py-3 bg-background-100 border-t border-background-200/70">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-center gap-2 text-sm font-medium text-accent-500 hover:text-accent-600 transition-colors duration-200"
                  >
                    View All Categories on Main Site
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </a>
                </div>
              </div>
            </div>

            <Link
              to="/pandabuy-sheet-guide"
              className="text-foreground-800 hover:text-foreground-950 text-sm font-semibold transition-colors duration-200 whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Pandabuy Sheet
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer flex-shrink-0"
            aria-label="Toggle menu"
          >
            <i className={`text-xl text-foreground-950 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-6 bg-background-50 border-t border-background-100">
          <div className="flex flex-col pt-3">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="px-4 py-3 text-foreground-950 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors duration-200"
            >
              <i className="ri-home-4-line mr-2.5"></i>
              Home
            </Link>

            <Link
              to="/pandabuy-sheet-guide"
              onClick={closeMobileMenu}
              className="px-4 py-3 text-foreground-950 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors duration-200"
            >
              <i className="ri-book-open-line mr-2.5"></i>
              Shopping Guide
            </Link>

            {/* Mobile Categories Toggle */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-foreground-950 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            >
              <span>
                <i className="ri-grid-line mr-2.5"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Mobile Categories List */}
            <div
              className={`overflow-hidden transition-all duration-300 ${
                mobileCatOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="grid grid-cols-2 gap-1 px-2 pt-2 pb-3">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-2 px-3 py-2.5 text-foreground-700 text-xs rounded-lg hover:bg-accent-50 hover:text-accent-500 transition-colors duration-200 truncate"
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobileMenu}
              className="mt-3 mx-2 px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full text-center hover:bg-primary-600 transition-all duration-200"
            >
              Pandabuy Sheet →
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}